/**
 * Real-time document edge detection using canvas-based Sobel gradient.
 *
 * Periodically samples the video feed (every 500ms), draws to a small
 * offscreen canvas (320x240), converts to grayscale, applies a Sobel-like
 * gradient magnitude, and checks 4 border regions for edge density. When
 * all 4 borders exceed the density threshold the document is considered
 * "detected".
 *
 * Performance:
 * - Uses requestAnimationFrame with a 500ms throttle (not every frame)
 * - Processes at 320x240 regardless of video resolution
 * - No Web Workers -- all computation happens on the main thread
 *
 * @module hooks/useDocumentEdgeDetection
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface EdgeDetectionState {
  /** True when all 4 border regions have sufficient edge density. */
  isDetected: boolean;
  /** Per-border detection results. */
  borders: { top: boolean; right: boolean; bottom: boolean; left: boolean };
  /** Overall confidence 0-1 (average of 4 border densities, clamped). */
  confidence: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Width of the offscreen analysis canvas. */
const ANALYSIS_WIDTH = 320;
/** Height of the offscreen analysis canvas. */
const ANALYSIS_HEIGHT = 240;

/** Minimum interval between analyses (milliseconds). */
const SAMPLE_INTERVAL_MS = 500;

/** Gradient magnitude threshold for counting a pixel as an "edge". */
const EDGE_MAGNITUDE_THRESHOLD = 30;

/** Each border region extends this fraction of the dimension inward. */
const BORDER_STRIP_RATIO = 0.15;

/**
 * Minimum fraction of edge pixels within a border strip for it to be
 * considered as having a document edge.
 */
const EDGE_DENSITY_THRESHOLD = 0.20;

/**
 * Number of consecutive "fully detected" frames before the document
 * is considered stable (for auto-capture).
 */
const STABLE_FRAME_COUNT = 3; // 3 * 500ms = 1.5s

// ---------------------------------------------------------------------------
// Image processing (pure functions)
// ---------------------------------------------------------------------------

/**
 * Convert RGBA pixel data to single-channel grayscale using BT.601.
 * Returns a Uint8Array for memory efficiency at analysis resolution.
 */
function toGrayscale(data: Uint8ClampedArray, width: number, height: number): Uint8Array {
  const gray = new Uint8Array(width * height);
  for (let i = 0; i < gray.length; i++) {
    const o = i * 4;
    gray[i] = Math.round(0.299 * data[o] + 0.587 * data[o + 1] + 0.114 * data[o + 2]);
  }
  return gray;
}

/**
 * Compute Sobel gradient magnitude at each pixel.
 * Returns a Uint8Array of magnitude values (clamped to 0-255).
 *
 * Gx = pixel(x+1,y) - pixel(x-1,y)
 * Gy = pixel(x,y+1) - pixel(x,y-1)
 * magnitude = sqrt(Gx^2 + Gy^2)
 */
function sobelMagnitude(gray: Uint8Array, width: number, height: number): Uint8Array {
  const mag = new Uint8Array(width * height);

  // Skip 1px border to avoid boundary checks
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const gx = gray[idx + 1] - gray[idx - 1];
      const gy = gray[idx + width] - gray[idx - width];
      mag[idx] = Math.min(255, Math.round(Math.sqrt(gx * gx + gy * gy)));
    }
  }

  return mag;
}

/**
 * Count the fraction of pixels above the edge threshold within a
 * rectangular region of the magnitude map.
 */
function edgeDensity(
  mag: Uint8Array,
  width: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): number {
  let count = 0;
  let total = 0;

  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      total++;
      if (mag[y * width + x] > EDGE_MAGNITUDE_THRESHOLD) {
        count++;
      }
    }
  }

  return total > 0 ? count / total : 0;
}

/**
 * Analyse a single video frame and return edge detection state.
 */
function analyseFrame(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): EdgeDetectionState {
  const w = canvas.width;
  const h = canvas.height;

  ctx.drawImage(video, 0, 0, w, h);
  const imageData = ctx.getImageData(0, 0, w, h);
  const gray = toGrayscale(imageData.data, w, h);
  const mag = sobelMagnitude(gray, w, h);

  // Define border strip boundaries.
  // The strips correspond to where the document overlay frame edges
  // would appear -- roughly in the central 80% of the frame
  // (matching the 80% width overlay in DocumentCapture).
  const frameMarginX = Math.round(w * 0.10); // overlay starts at 10% from each side
  const frameMarginY = Math.round(h * 0.10);
  const frameRight = w - frameMarginX;
  const frameBottom = h - frameMarginY;

  const stripW = Math.round((frameRight - frameMarginX) * BORDER_STRIP_RATIO);
  const stripH = Math.round((frameBottom - frameMarginY) * BORDER_STRIP_RATIO);

  // Top strip: full width of frame area, top portion
  const topDensity = edgeDensity(mag, w, frameMarginX, frameMarginY, frameRight, frameMarginY + stripH);
  // Bottom strip
  const bottomDensity = edgeDensity(mag, w, frameMarginX, frameBottom - stripH, frameRight, frameBottom);
  // Left strip: full height of frame area, left portion
  const leftDensity = edgeDensity(mag, w, frameMarginX, frameMarginY, frameMarginX + stripW, frameBottom);
  // Right strip
  const rightDensity = edgeDensity(mag, w, frameRight - stripW, frameMarginY, frameRight, frameBottom);

  const borders = {
    top: topDensity >= EDGE_DENSITY_THRESHOLD,
    right: rightDensity >= EDGE_DENSITY_THRESHOLD,
    bottom: bottomDensity >= EDGE_DENSITY_THRESHOLD,
    left: leftDensity >= EDGE_DENSITY_THRESHOLD,
  };

  const detectedCount = [borders.top, borders.right, borders.bottom, borders.left].filter(Boolean).length;
  const isDetected = detectedCount === 4;

  // Confidence: average of border densities normalised against the threshold
  const avgDensity = (topDensity + rightDensity + bottomDensity + leftDensity) / 4;
  const confidence = Math.min(1, avgDensity / EDGE_DENSITY_THRESHOLD);

  return { isDetected, borders, confidence };
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export interface UseDocumentEdgeDetectionOptions {
  /** Reference to the active video element. */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Whether the detection loop should be running. */
  enabled: boolean;
  /** Enable auto-capture after stable detection (default false). */
  autoCapture?: boolean;
  /** Called when auto-capture fires. */
  onAutoCapture?: () => void;
}

const INITIAL_STATE: EdgeDetectionState = {
  isDetected: false,
  borders: { top: false, right: false, bottom: false, left: false },
  confidence: 0,
};

export function useDocumentEdgeDetection({
  videoRef,
  enabled,
  autoCapture = false,
  onAutoCapture,
}: UseDocumentEdgeDetectionOptions): EdgeDetectionState {
  const [state, setState] = useState<EdgeDetectionState>(INITIAL_STATE);

  // Offscreen canvas for analysis (created once, reused)
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const rafRef = useRef<number>(0);
  const lastAnalysisRef = useRef<number>(0);
  const stableCountRef = useRef<number>(0);
  const autoCapturedRef = useRef<boolean>(false);

  // Keep onAutoCapture in a ref to avoid re-triggering the effect
  const onAutoCaptureRef = useRef(onAutoCapture);
  onAutoCaptureRef.current = onAutoCapture;

  const tick = useCallback(() => {
    const now = performance.now();

    if (now - lastAnalysisRef.current < SAMPLE_INTERVAL_MS) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    lastAnalysisRef.current = now;

    const video = videoRef.current;
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    // Lazily create the offscreen canvas
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
      canvasRef.current.width = ANALYSIS_WIDTH;
      canvasRef.current.height = ANALYSIS_HEIGHT;
      ctxRef.current = canvasRef.current.getContext('2d', { willReadFrequently: true });
    }

    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    if (!ctx || !canvas) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    const result = analyseFrame(ctx, video, canvas);
    setState(result);

    // Track stability for auto-capture
    if (result.isDetected) {
      stableCountRef.current++;
    } else {
      stableCountRef.current = 0;
      autoCapturedRef.current = false;
    }

    // Fire auto-capture once after stable detection
    if (
      autoCapture &&
      !autoCapturedRef.current &&
      stableCountRef.current >= STABLE_FRAME_COUNT &&
      onAutoCaptureRef.current
    ) {
      autoCapturedRef.current = true;
      onAutoCaptureRef.current();
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [videoRef, autoCapture]);

  useEffect(() => {
    if (!enabled) {
      setState(INITIAL_STATE);
      stableCountRef.current = 0;
      autoCapturedRef.current = false;
      return;
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [enabled, tick]);

  return state;
}
